<template>
  <VeeForm v-slot="{ errors, handleSubmit, meta }" class="w-full">
    <VeeField
      v-slot="{ field, errorMessage }"
      v-model="formModel.price"
      name="price"
    >
      <NText
        variant="caption1"
        weight="medium"
        class="text-neutral-secondary-press block pr-3 mb-2"
      >
        {{ t('modules.portfolio.editNetAveragePrice') }}
      </NText>

      <NInput
        v-mask="{
          alias: 'positiveNumber',
        }"
        :model-value="formModel.price"
        v-bind="field"
        class="my-2"
        type="tel"
        :error="errorMessage || errors.price"
        show-error
        @focusin="$event.target.select()"
      />

      <NText
        variant="caption2"
        weight="regular"
        class="text-neutral-primary block"
      >
        {{ t('modules.portfolio.editNetAveragePriceDescription') }}
      </NText>

      <div class="flex mt-4">
        <NButton
          v-gid="{
            module: 'order',
            screen: 'home',
            section: 'portfolioEditDialog',
            component: 'portfolioEditDialogSubmitBtn',
          }"
          :disabled="
            isLoadingUpdateNetAveragePrice ||
            !meta.valid ||
            selectedSymbol.netAveragePrice === formModel.price ||
            !formModel.price ||
            formModel.price <= 0
          "
          class="me-3 w-1/2"
          @click="handleSubmit($event, submit)"
        >
          <NText weight="bold" variant="caption1">
            {{ t('misc.submit') }}
          </NText>
        </NButton>

        <NButton
          variant="plain"
          color="gray"
          class="w-1/2"
          @click.prevent="remove()"
        >
          <NText weight="bold" variant="caption1">
            {{ t('common.cancel') }}
          </NText>
        </NButton>
      </div>
    </VeeField>
  </VeeForm>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { reactive } from 'vue';
import { PortfolioItem } from '@libs/api-reference';
import { useDialog, useToast } from '@negar/core';
import { useQueryClient } from '@libs/use';
import {
  PORTFOLIO_QUERY_KEYS,
  useUpdateNetAveragePriceQuery,
} from '@core/portfolio';

import vMask from '@/directives/mask';

type EditNetAveragePriceProps = {
  selectedSymbol: PortfolioItem;
};

const props = defineProps<EditNetAveragePriceProps>();

const { t } = useI18n();

const { remove } = useDialog();
const toast = useToast();

const queryClient = useQueryClient();

const { updateNetAveragePrice, isLoadingUpdateNetAveragePrice } =
  useUpdateNetAveragePriceQuery({
    isin: props.selectedSymbol.instrumentIsin,
    onSuccess() {
      toast.neutral({
        message: t('modules.portfolio.editNetAveragePriceXSuccessMessage', {
          name: props.selectedSymbol.instrumentName,
        }),
        duration: 3000,
      });

      queryClient.invalidateQueries(PORTFOLIO_QUERY_KEYS.portfolio);

      remove();
    },
    onReject() {
      toast.negative({
        message: t('modules.portfolio.editNetAveragePriceXFailedMessage', {
          name: props.selectedSymbol.instrumentName,
        }),
        duration: 3000,
      });
    },
  });

const formModel = reactive({
  price: Math.round(props.selectedSymbol.netAveragePrice),
});

const submit = (formModel) => {
  updateNetAveragePrice({ price: Math.round(formModel.price) });
};
</script>
